// apps/web/src/pages/estoque/fechar-rateio-dialog.tsx — Fase 13, Plano 32 (EST-15, D-02, D-03).
// Confirmação de "Fechar rateio do dia". Fechar o rateio é auditado mas NÃO destrutivo (UI-SPEC): botão primário, nunca
// vermelho. O diálogo busca a distribuição do dia para dizer entre quantos lotes o consumo será distribuído, fica aberto
// quando a API devolve erro (com a mensagem) e trava os botões, com spinner, durante o envio.
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { DistribuicaoRateio } from '@pieltec/contracts';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Skeleton } from '../../components/ui/skeleton';
import { textoConfirmacaoFechamento } from '../../lib/estoque/consumo-tinta';

export interface AlvoFechamentoRateio {
  /** YYYY-MM-DD */
  readonly dia: string;
  readonly materialId: string;
  /** Cor (ou código da tinta) como aparece na linha. */
  readonly cor: string;
}

export interface FecharRateioDialogProps {
  /** `null` = fechado. */
  readonly alvo: AlvoFechamentoRateio | null;
  readonly enviando: boolean;
  /** Mensagem do erro da API: o diálogo continua aberto. */
  readonly erro: string | null;
  readonly aoConfirmar: () => void;
  readonly aoFechar: () => void;
}

export function FecharRateioDialog({ alvo, enviando, erro, aoConfirmar, aoFechar }: FecharRateioDialogProps) {
  const [lotes, setLotes] = useState<number | null>(null);
  const [falhaDetalhe, setFalhaDetalhe] = useState(false);

  const dia = alvo?.dia;
  const materialId = alvo?.materialId;

  // Lotes que recebem o consumo = os que têm área unitária (os sem área ficam fora do rateio).
  useEffect(() => {
    setLotes(null);
    setFalhaDetalhe(false);
    if (!dia || !materialId) return;
    let vivo = true;
    const params = new URLSearchParams({ dia, materialId });
    fetch(`/api/v1/estoque/tinta/rateios/detalhe?${params.toString()}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<DistribuicaoRateio>;
      })
      .then((json) => {
        if (vivo) setLotes(json.lotes.filter((lote) => !lote.semArea).length);
      })
      .catch(() => {
        if (vivo) setFalhaDetalhe(true);
      });
    return () => {
      vivo = false;
    };
  }, [dia, materialId]);

  return (
    <Dialog open={alvo !== null} onOpenChange={(proximo) => !proximo && !enviando && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Fechar rateio</DialogTitle>
          <DialogDescription asChild>
            <div>
              {alvo === null ? null : lotes !== null ? (
                <>
                  {textoConfirmacaoFechamento({ cor: alvo.cor, dia: alvo.dia, lotes })} Depois de fechado, só se corrige por estorno.
                </>
              ) : falhaDetalhe ? (
                <span className="text-rose-700">Não foi possível carregar a distribuição do dia. Feche este aviso e tente novamente.</span>
              ) : (
                <Skeleton className="h-10 w-full" aria-label="Carregando distribuição do dia" />
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        {erro && (
          <div role="alert">
            <FormMessage>{erro}</FormMessage>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" disabled={enviando} onClick={aoFechar}>
            Voltar
          </Button>
          <Button type="button" className="min-h-11 sm:min-h-9" disabled={enviando || lotes === null} onClick={aoConfirmar}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Fechar rateio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
